import { useTranslations } from 'next-intl';
import {
  isAuthRequired,
  markAuthRequiredLogoutRedirect,
} from '@/app/components/shared/Sidebar/_utils/is-auth-required';
import { toast } from '@/app/components/ui/use-toast';
import { HOME_PAGE, isEnterpriseDistribution } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { sensorsSignOut } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { usersLogout } from './apis';

/**
 * 用户退出登录的 Hook
 *
 * @example
 * ```tsx
 * const signOut = useSignOut();
 *
 * return <button onClick={() => signOut()}>退出登录</button>
 * ```
 *
 * @returns 退出登录的方法
 */
interface UseSignOutOptions {
  onSuccess?: () => void;
}

export const useSignOut = (options?: UseSignOutOptions) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const {
    updateUserInfo,
    resetTaskFormStates,
    resetToolFormStates,
    updateCreditsInfo,
    updateSubscription,
    updateAvailableNexus,
    updateIsNexusInitialized,
    setActiveClaw,
    setClawStatus,
    setClawInfoInitialized,
  } = useStore((state) => ({
    updateUserInfo: state.updateUserInfo,
    resetTaskFormStates: state.resetTaskFormStates,
    resetToolFormStates: state.resetToolFormStates,
    updateCreditsInfo: state.updateCreditsInfo,
    updateSubscription: state.updateSubscriptionInfo,
    updateAvailableNexus: state.updateAvailableNexus,
    updateIsNexusInitialized: state.updateIsNexusInitialized,
    setActiveClaw: state.setActiveClaw,
    setClawStatus: state.setClawStatus,
    setClawInfoInitialized: state.setClawInfoInitialized,
  }));

  const { run } = useFetch(() => usersLogout({ body: {} }), {
    manual: true,
    onSuccess: ({ error }) => {
      if (error) {
        toast({
          type: 'warning',
          title: t('popup.toast.signOutError'),
          message: error.message,
        });
        return;
      }
      sensorsSignOut();
      updateUserInfo(null);
      updateCreditsInfo(null);
      updateSubscription(null);
      updateAvailableNexus([]);
      updateIsNexusInitialized(false);
      resetTaskFormStates();
      resetToolFormStates();
      setActiveClaw(null);
      setClawStatus(null);
      setClawInfoInitialized(false);
      options?.onSuccess?.();
      // 仅在当前页面需要登录时跳转，免登录页面退出后停留当前页
      if (isAuthRequired(pathname)) {
        markAuthRequiredLogoutRedirect();
        if (isEnterpriseDistribution) {
          window.location.replace('/login');
        } else {
          router.replace(HOME_PAGE);
        }
      }
    },
  });

  return run;
};
